import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { IonInput, IonItem, IonList } from '@ionic/react';
import { Controller } from 'react-hook-form';
import classnames from 'classnames';
import useTheme from '../../../../../hooks/ui/useTheme';
import { usePlacesWidget } from 'react-google-autocomplete';
import { GooglePlaceResource } from '../../../../../services/resources/GooglePlaceResource';
import { useSelector } from 'react-redux';
import useDeliveryForm from './useDeliveryForm';
import { DeliveryAddressSchema } from './schema';
import useGetDeliveryLocation from '../../../common/hooks/useGetDeliveryLocation';
import SearchIcon from '../../../../../assets/icons/actions/SearchIcon';
import { useTranslation } from 'react-i18next';
import { selectCompany } from '../../../../../store/slices/InitialDataSlice';
import useEnv from '../../../../../hooks/utility/useEnv';

interface DeliveryAddressFormProps {
  defaultValues?: DeliveryAddressSchema;
  validationError?: string;
  onSubmit?: (data: DeliveryAddressSchema) => void;
}

export interface HandleDeliveryFormRef {
  submit: () => void;
}

interface FormItem {
  key: keyof DeliveryAddressSchema;
  placeholder: string;
}

const formItems: FormItem[] = [
  {
    key: 'delivery_street_name',
    placeholder: 'placeholders.delivery_street_name',
  },
  { key: 'delivery_street_number', placeholder: 'placeholders.delivery_street_number' },
  { key: 'delivery_city', placeholder: 'placeholders.delivery_city' },
  { key: 'delivery_zip_code', placeholder: 'placeholders.delivery_zip_code' },
];

const DeliveryAddressForm = forwardRef<HandleDeliveryFormRef, DeliveryAddressFormProps>(
  ({ defaultValues, onSubmit }, formRef) => {
    const { t } = useTranslation(null, { keyPrefix: 'Modules:Mobile:Modals:DeliveryAddress:DeliveryAddressForm' });
    const company = useSelector(selectCompany);
    const { VITE_GOOGLE_MAPS_API_KEY } = useEnv();
    const form = useDeliveryForm({ defaultValues });
    const { getLocation, validationError, loading } = useGetDeliveryLocation(company);
    const theme = useTheme();
    const selectedPlace = useRef<GooglePlaceResource>(null);
    const { ref } = usePlacesWidget({
      apiKey: VITE_GOOGLE_MAPS_API_KEY,
      onPlaceSelected: (place) => {
        const selectedPlaceResource = new GooglePlaceResource(place);

        form.setValue('delivery_street_name', selectedPlaceResource.getStreetName());
        form.setValue('delivery_street_number', selectedPlaceResource.getStreetNumber());
        form.setValue('delivery_city', selectedPlaceResource.getCity());
        form.setValue('delivery_zip_code', selectedPlaceResource.getPostalCode());
        form.setValue('delivery_lat', selectedPlaceResource.getLat());
        form.setValue('delivery_lng', selectedPlaceResource.getLng());

        selectedPlace.current = selectedPlaceResource;
      },
      options: {
        types: [],
        componentRestrictions: {
          country: company?.country,
        },
      },
    });

    const onSubmitForm = async (data: DeliveryAddressSchema): Promise<void> => {
      if (loading) {
        return;
      }

      const { success } = await getLocation(data, selectedPlace.current);

      if (!success) {
        return;
      }

      onSubmit && onSubmit(data);
    };

    useImperativeHandle(
      formRef,
      () => ({
        submit: form.handleSubmit(onSubmitForm),
      }),
      [form.handleSubmit],
    );

    return (
      <>
        <form onSubmit={form.handleSubmit(onSubmitForm)} className="py-md space-y-md">
          <IonList lines="none" className="space-y-md">
            <IonItem>
              <fieldset className="bordered border border-brand-primary size-full flex items-center px-md">
                <input
                  className="w-full placeholder:text-brand-text-secondary"
                  placeholder={t('placeholders.type_your_address')}
                  ref={ref}
                />
                <label>
                  <SearchIcon className="size-4 text-sm" color={theme.color.brand.primary} />
                </label>
              </fieldset>
            </IonItem>
            {formItems.map((item) => (
              <IonItem className="overflow-visible" key={item.key}>
                <Controller
                  name={item.key}
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <div className="w-full">
                      <IonInput
                        type="email"
                        className={classnames('bordered !px-4', {
                          'border-danger border-xs': !!fieldState.error?.message,
                        })}
                        style={{ '--placeholder-color': theme.color.mobile.tertiary }}
                        placeholder={t(item.placeholder)}
                        onIonChange={field.onChange}
                        {...field}
                      />
                      <p className="text-danger text-xs h-3">{fieldState.error?.message}</p>
                    </div>
                  )}
                />
              </IonItem>
            ))}
          </IonList>
          <p className="text-danger text-xs h-3 text-center px-md">{validationError}</p>
        </form>
      </>
    );
  },
);

export default DeliveryAddressForm;
